import JsBarcode from "jsbarcode"
import QRCode from "qrcode"
import { buildLabelPrintPlan } from "./boxLabelData.js"
import { buildProcessingReceiptFromOrder } from "../fish-room/buildProcessingReceiptFromOrder.js"
import { buildProcessingReceiptPdf } from "../fish-room/buildProcessingReceiptPdf.js"
import { renderProcessingReceiptPdfForPrint, PDF_PRINT_SECTION_STYLES } from "../fish-room/renderProcessingReceiptPdfForPrint.js"
import {
    FISH_TAG_BADGE_SIZE_PX,
    FISH_TAG_CIRCLE_FONT_SIZE_PX,
    FISH_TAG_PILL_FONT_SIZE_PX,
    formatFishTagPillText,
    isLongFishTag,
    splitBoxLabelFishTags,
} from "./fishTagBadges.js"

// ─── Layout / print tuning constants ────────────────────────────────────────

/** Barcode SVG viewBox (matches PDF box labels, 450×120 design units). */
const BARCODE_VIEW_WIDTH = 450
const BARCODE_VIEW_HEIGHT = 120

/** QR code pixel width. */
const QR_CODE_SIZE_PX = 140

/** Customer name ("LAST, FIRST") font sizes. Long names shrink after 14 characters. */
const CUSTOMER_NAME_FONT_SIZE_PX = 34
const CUSTOMER_NAME_LONG_FONT_SIZE_PX = 28
const CUSTOMER_NAME_LONG_MIN_CHARS = 15

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
}

/** @returns {string | null} Module pattern from JsBarcode ("1" = bar, "0" = gap). */
function getMasterBarcodeModules(value) {
    const text = String(value ?? "").trim()
    if (!text) {
        return null
    }

    try {
        const data = {}
        JsBarcode(data, text, {
            format: "CODE128",
            displayValue: false,
            margin: 0,
        })
        return data.encodings?.[0]?.data ?? null
    } catch {
        return null
    }
}

/**
 * Fixed-aspect barcode SVG — module width scales to fill the label width while bar
 * height stays constant. Avoids very tall barcodes when the master number is short
 * (JsBarcode's native SVG is only as wide as the encoded value, so width:100% +
 * height:auto stretches short codes vertically).
 */
function barcodeDataUrl(value) {
    if (typeof document === "undefined") {
        return null
    }

    const modules = getMasterBarcodeModules(value)
    if (!modules) {
        return null
    }

    const moduleWidth = BARCODE_VIEW_WIDTH / modules.length
    const rects = []
    let x = 0

    for (let i = 0; i < modules.length; i++) {
        if (modules[i] === "1") {
            rects.push(
                `<rect x="${x}" y="0" width="${moduleWidth}" height="${BARCODE_VIEW_HEIGHT}" fill="#000"/>`
            )
        }
        x += moduleWidth
    }

    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" ` +
        `viewBox="0 0 ${BARCODE_VIEW_WIDTH} ${BARCODE_VIEW_HEIGHT}" ` +
        `width="${BARCODE_VIEW_WIDTH}" height="${BARCODE_VIEW_HEIGHT}">` +
        `${rects.join("")}</svg>`

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

async function qrDataUrl(payload) {
    try {
        return await QRCode.toDataURL(String(payload ?? ""), {
            errorCorrectionLevel: "M",
            margin: 1,
            width: QR_CODE_SIZE_PX,
        })
    } catch {
        return null
    }
}

function buildFishTagCircleHtml(tag) {
    const clean = String(tag ?? "").trim()
    if (!clean) {
        return ""
    }
    const sizePx = FISH_TAG_BADGE_SIZE_PX
    const fontSize = FISH_TAG_CIRCLE_FONT_SIZE_PX
    return `<div class="bl-tag-circle" style="width:${sizePx}px;height:${sizePx}px;min-width:${sizePx}px;min-height:${sizePx}px;max-width:${sizePx}px;max-height:${sizePx}px;"><span class="bl-tag-circle-text" style="font-size:${fontSize}px;">${escapeHtml(clean)}</span></div>`
}

function buildFishTagPillHtml(tag) {
    const displayText = formatFishTagPillText(tag)
    if (!displayText) {
        return ""
    }
    const sizePx = FISH_TAG_BADGE_SIZE_PX
    return `<div class="bl-tag-pill" style="min-height:${sizePx}px;height:${sizePx}px;max-height:${sizePx}px;"><span class="bl-tag-pill-text" style="font-size:${FISH_TAG_PILL_FONT_SIZE_PX}px;">${escapeHtml(displayText)}</span></div>`
}

function buildFishTagItemHtml(tag) {
    return isLongFishTag(tag) ? buildFishTagPillHtml(tag) : buildFishTagCircleHtml(tag)
}

function buildFishTagGroupHtml(fishTagRaw) {
    const tags = splitBoxLabelFishTags(fishTagRaw)
    if (tags.length === 0) {
        return ""
    }
    return `<div class="bl-tag-group">${tags.map((tag) => buildFishTagItemHtml(tag)).join("")}</div>`
}

function customerNameFontSizePx(name) {
    return String(name ?? "").length >= CUSTOMER_NAME_LONG_MIN_CHARS
        ? CUSTOMER_NAME_LONG_FONT_SIZE_PX
        : CUSTOMER_NAME_FONT_SIZE_PX
}

const BOX_LABEL_V2_STYLES = `
  @page { size: 4in 6in; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Helvetica, Arial, sans-serif;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .bl-label {
    width: 4in;
    height: 6in;
    padding: 0.22in 0.2in 0.2in;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .bl-label + .bl-label {
    page-break-before: always;
    break-before: page;
  }
  .bl-head { text-align: center; flex-shrink: 0; }
  .bl-master {
    font-size: 26px;
    font-weight: 900;
    letter-spacing: 0.03em;
    margin-bottom: 8px;
    line-height: 1.1;
    word-break: break-word;
  }
  .bl-barcode-wrap { width: 100%; }
  .bl-barcode-img { width: 100%; height: auto; display: block; }
  .bl-barcode-fallback { font-size: 10px; padding: 8px 0; font-family: 'Courier New', Courier, monospace; }
  .bl-name {
    flex-shrink: 0;
    text-align: center;
    font-weight: 900;
    line-height: 1.1;
    letter-spacing: 0.02em;
    padding: 0.1in 0.05in 0;
    margin-top: 4px;
    word-break: break-word;
  }
  .bl-license {
    flex-shrink: 0;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.04em;
    font-family: 'Courier New', Courier, monospace;
    margin-top: 6px;
    padding: 0 0.05in;
  }
  .bl-spacer { flex: 1; min-height: 0.08in; }
  .bl-bottom { flex-shrink: 0; display: flex; flex-direction: column; }
  .bl-status {
    margin-top: 0.08in;
    width: 100%;
    min-height: 0.72in;
    border: 2px solid #000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.06in 0.08in;
    flex-shrink: 0;
  }
  .bl-status-text {
    font-size: 28px;
    font-weight: 900;
    text-align: center;
    line-height: 1.05;
    word-break: break-word;
  }
  .bl-foot {
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.12in;
    margin-top: 0.1in;
    min-height: 1.35in;
  }
  .bl-qr-cell { flex-shrink: 0; width: 1.15in; }
  .bl-qr-img { width: 1.15in; height: 1.15in; display: block; }
  .bl-meta {
    text-align: right;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    min-height: 1.35in;
    padding-bottom: 2px;
  }
  .bl-tag-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    align-content: flex-end;
    gap: 5px;
    max-width: 2.2in;
    max-height: ${FISH_TAG_BADGE_SIZE_PX * 2 + 8}px;
    overflow: hidden;
    margin-bottom: auto;
    flex-shrink: 0;
  }
  .bl-tag-group > * { flex-shrink: 0; }
  .bl-tag-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #000;
    color: #fff;
    font-weight: 900;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1;
    text-align: center;
    padding: 0;
    flex-shrink: 0;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .bl-tag-circle-text {
    display: block;
    line-height: 1;
    text-align: center;
    margin: 0;
    padding: 0;
    font-weight: 900;
    font-family: 'Courier New', Courier, monospace;
    /* Courier numerals sit slightly low; nudge for optical center in print */
    transform: translateY(0.04em);
  }
  .bl-tag-pill {
    max-width: ${Math.round(FISH_TAG_BADGE_SIZE_PX * 3.2)}px;
    border-radius: ${Math.round(FISH_TAG_BADGE_SIZE_PX * 0.31)}px;
    background: #000;
    color: #fff;
    font-weight: 900;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1.05;
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    overflow: hidden;
    flex-shrink: 0;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .bl-tag-pill-text {
    display: block;
    white-space: nowrap;
  }
  .bl-trip {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.02em;
    margin-top: 5px;
    margin-bottom: 6px;
    line-height: 1.1;
    flex-shrink: 0;
  }
  .bl-location {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: 0.06em;
    margin-bottom: 2px;
    flex-shrink: 0;
  }
  .bl-box-line {
    font-size: 30px;
    font-weight: 900;
    line-height: 1;
    white-space: nowrap;
    flex-shrink: 0;
  }
  ${PDF_PRINT_SECTION_STYLES}
`

function buildLabelPageHtml(label, barcodeSrc, qrSrc) {
    const barcodeBlock = barcodeSrc
        ? `<div class="bl-barcode-wrap"><img src="${escapeHtml(barcodeSrc)}" alt="" class="bl-barcode-img" /></div>`
        : `<div class="bl-barcode-fallback">${escapeHtml(label.master)}</div>`

    const qrBlock = qrSrc
        ? `<img src="${escapeHtml(qrSrc)}" alt="" class="bl-qr-img" />`
        : ""

    const statusBlock = label.status
        ? `<div class="bl-status"><div class="bl-status-text">${escapeHtml(label.status)}</div></div>`
        : ""

    const fishTagGroup = buildFishTagGroupHtml(label.fishTagRaw)
    const tripBlock = label.tripCode
        ? `<div class="bl-trip">trip: ${escapeHtml(label.tripCode)}</div>`
        : ""

    return `<section class="bl-label">
  <header class="bl-head">
    <div class="bl-master">${escapeHtml(label.master)}</div>
    ${barcodeBlock}
  </header>
  <div class="bl-name" style="font-size:${customerNameFontSizePx(label.customerName)}px;">${escapeHtml(label.customerName)}</div>
  <div class="bl-license">Fishing Lic. ${escapeHtml(label.fishingLicense)}</div>
  <div class="bl-spacer" aria-hidden="true"></div>
  <div class="bl-bottom">
    ${statusBlock}
    <footer class="bl-foot">
      <div class="bl-qr-cell">${qrBlock}</div>
      <div class="bl-meta">
        ${fishTagGroup}
        ${tripBlock}
        <div class="bl-location">${escapeHtml(label.locationCode)}</div>
        <div class="bl-box-line">Box ${label.boxNum} of ${label.totalBoxes}</div>
      </div>
    </footer>
  </div>
</section>`
}

function printHtmlDocument(html) {
    if (typeof document === "undefined") {
        return
    }

    const iframe = document.createElement("iframe")
    iframe.setAttribute("aria-hidden", "true")
    Object.assign(iframe.style, {
        position: "fixed",
        right: "0",
        bottom: "0",
        width: "0",
        height: "0",
        border: "none",
        opacity: "0",
        pointerEvents: "none",
    })
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) {
        iframe.remove()
        return
    }

    let cleaned = false
    const cleanup = () => {
        if (cleaned) {
            return
        }
        cleaned = true
        iframe.remove()
    }

    win.addEventListener("afterprint", cleanup)

    doc.open()
    doc.write(html)
    doc.close()

    const triggerPrint = () => {
        try {
            win.focus()
            win.print()
        } finally {
            setTimeout(cleanup, 2000)
        }
    }

    const fontsReady =
        doc.fonts && typeof doc.fonts.ready?.then === "function"
            ? doc.fonts.ready
            : Promise.resolve()

    Promise.resolve(fontsReady)
        .catch(() => { })
        .finally(() => {
            setTimeout(triggerPrint, 50)
        })
}

async function buildLabelPageHtmlAsync(label) {
    const barcodeSrc = barcodeDataUrl(label.master)
    const qrSrc = await qrDataUrl(label.qrValue)
    return buildLabelPageHtml(label, barcodeSrc, qrSrc)
}

/**
 * Print 4×6 box labels via the browser print dialog (HTML iframe).
 * Page order matches the PDF implementation: per-order box pages (with copies),
 * with processing receipts interleaved after each order when requested.
 *
 * @param {{
 *   orders: any[],
 *   locations?: any[],
 *   helpers?: any,
 *   numCopies?: number,
 *   printProcessingReceipt?: boolean,
 *   app?: any,
 *   cancelRef?: { current: boolean } | null,
 *   onProgress?: (value: number) => void,
 * }} options
 */
export async function printBoxLabelsV2({
    orders,
    locations = [],
    helpers = {},
    numCopies = 1,
    printProcessingReceipt = false,
    app = null,
    cancelRef = null,
    onProgress = () => { },
}) {
    const isCancelled = () => Boolean(cancelRef?.current)

    if (cancelRef) {
        cancelRef.current = false
    }

    onProgress(0)

    const { labelList, segments } = buildLabelPrintPlan(
        orders,
        locations,
        helpers,
        numCopies
    )

    if (labelList.length === 0) {
        console.warn("BoxLabels v2: no labels to print — check total_boxes on selected orders.")
        onProgress(100)
        return
    }

    const sections = []
    let labelIndex = 0

    for (const segment of segments) {
        if (isCancelled()) {
            return
        }

        for (let i = 0; i < segment.pageCount; i++) {
            const label = labelList[labelIndex]
            labelIndex += 1
            if (!label) {
                break
            }
            sections.push(await buildLabelPageHtmlAsync(label))
        }

        if (printProcessingReceipt && segment.order) {
            const receipt = buildProcessingReceiptFromOrder(
                segment.order,
                segment.order?.total_boxes,
                app,
                helpers
            )
            const receiptBytes = await buildProcessingReceiptPdf(receipt)
            if (isCancelled()) {
                return
            }
            sections.push(await renderProcessingReceiptPdfForPrint(receiptBytes))
        }
    }

    if (isCancelled()) {
        return
    }

    onProgress(100)

    const title = printProcessingReceipt ? "Box labels & processing receipts" : "Box labels"
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title><style>${BOX_LABEL_V2_STYLES}</style></head><body>${sections.join("")}</body></html>`

    printHtmlDocument(html)
}
